import {BrowserDetection, UrlService} from "@nuskin/ns-util";
import {AccountManager} from '@nuskin/ns-account';
import {PriceType,Product} from '@nuskin/ns-product-lib';
import { getCachedConfigField } from "@nuskin/configuration-sdk";

export default function CartItem(cartItemData) {
    if (!cartItemData.product) {
        return;
    }

    // ---------------------------------------------
    //
    //        Define all private methods.
    //
    // ---------------------------------------------
    function setCanDelete(canDelete, isBp) {
        return canDelete === true || canDelete === false ? canDelete : isBp !== true;
    }

    function ensureIntVal(val) {
        let intVal = 0;
        if (typeof val === 'number') {
            intVal = val;
        }
        else if (typeof val === 'string') {
            intVal = parseInt(val);
        }
        return intVal;
    }

    this.id = cartItemData.id || ''; // persistent line id
    this.key = cartItemData.key != undefined ? cartItemData.key : null;

    this.product = new Product(cartItemData.product);

    const siteUrl = UrlService.getSiteUrl();
    const isEdge = BrowserDetection.isEdge();
    const isFirefox = BrowserDetection.isFirefox();

    // set the base url for images, etc
    this.product.setBaseUrl(siteUrl);

    // fix image paths for non-edge/firefox
    if (!isEdge && !isFirefox) {
        this.product.fullImage = this.product.fullImage
            ? `${this.product.fullImage.split("?")[0]}?format=pjpg`
            : undefined;
        this.product.thumbnail = this.product.thumbnail
            ? `${this.product.thumbnail.split("?")[0]}?format=pjpg`
            : undefined;
    }

    this.qty = cartItemData.qty;
    this.maxQty = cartItemData.maxQty || 999;
    this.adr = cartItemData.adr || false;
    this.businessPortfolio = cartItemData.businessPortfolio || false;
    this.alternateBusinessPortfolios = [];
    this.sapLineId = cartItemData.sapLineId || null;
    this.canDelete = setCanDelete(cartItemData.canDelete, this.businessPortfolio);
    this.redeem = cartItemData.redeem;
    this.partialRedeem = cartItemData.partialRedeem;
    this.qtyRedeemWithPoints = cartItemData.qtyRedeemWithPoints || 0;
    this.loyaltyPointsUsed = cartItemData.loyaltyPointsUsed;

    if (cartItemData.alternateBusinessPortfolios) {
        for (let bpi = 0; bpi < cartItemData.alternateBusinessPortfolios.length; bpi++) {
            this.alternateBusinessPortfolios.push(new Product(cartItemData.alternateBusinessPortfolios[bpi]));
        }
    }

    // ---------------------------------------------
    //
    //        Define all public methods.
    //
    // ---------------------------------------------
    this.toJSON = function () {
        let item = {},
            alternateBPs = [];

        item.id = this.id;
        item.key = this.key;
        item.product = this.product.toJSON();
        item.adr = this.adr;
        item.qty = this.qty;
        item.points = this.points;
        item.loyaltyPointsUsed = this.loyaltyPointsUsed;
        item.maxQty = this.maxQty;
        item.businessPortfolio = this.businessPortfolio;
        item.sapLineId = this.sapLineId;
        item.canDelete = this.canDelete;
        item.redeem = this.redeem;
        item.partialRedeem = this.partialRedeem;
        item.qtyRedeemWithPoints = this.qtyRedeemWithPoints;

        for (let i = 0; i < this.alternateBusinessPortfolios.length; i++) {
            alternateBPs.push(this.alternateBusinessPortfolios[i].toJSON());
        }
        if (alternateBPs.length > 0) {
            item.alternateBusinessPortfolios = alternateBPs;
        }

        return item;
    };

    this.setProduct = function (product) {
        this.product = new Product(product);
    };

    this.setQty = function (qty) {
        this.qty = ensureIntVal(qty);
    };

    this.setRedeem = function (redeem) {
        this.redeem = redeem;
    };

    this.setPartialRedeem = function (redeem) {
        this.partialRedeem = redeem;
    };

    this.setQtyRedeemWithPoints = function (qty) {
        this.qtyRedeemWithPoints = qty;
    };

    this.setMaxQty = function (maxQty) {
        this.maxQty = ensureIntVal(maxQty);
    };

    this.setAdr = function (isAdr) {
        this.adr = isAdr;
    };

    this.setBusinessPortfolio = function (businessPortfolio) {
        this.businessPortfolio = businessPortfolio;
    };

    this.addAlternateBusinessPortfolio = function (altBpProduct) {
        if (altBpProduct instanceof Product) {
            const priceType = getCachedConfigField('showWholeSalePricing') && !AccountManager.isLoggedIn()
                ? PriceType.WWHL
                : this.product.priceType;
            altBpProduct.setPriceAndPvFromType(priceType);
            this.alternateBusinessPortfolios.push(altBpProduct);
        }
    };

    this.setSapLineId = function (sapLineId) {
        this.sapLineId = sapLineId;
    };

    this.getSapLineId = function () {
        return this.sapLineId;
    };

    this.replaceBpWithAltBp = function (altBpSku) {
        let bpProduct = this.product,
            newAltBps = [],
            altBp;

        for (let i = 0; i < this.alternateBusinessPortfolios.length; i++) {
            altBp = this.alternateBusinessPortfolios[i];
            if (altBp.sku != altBpSku) {
                newAltBps.push(altBp);
            } else {
                this.product = altBp;
            }
        }
        newAltBps.push(bpProduct);
        this.alternateBusinessPortfolios = newAltBps;
    };

    this.getAltBusinessPortfolios = function () {
        return this.alternateBusinessPortfolios;
    };

    this.getProduct = function () {
        return this.product;
    };

    this.getQty = function () {
        return parseInt(this.qty);
    };

    this.getQtyRedeemWithPoints = function () {
        return parseInt(this.qtyRedeemWithPoints);
    };

    this.getMaxQty = function () {
        return this.maxQty;
    };

    this.getSku = function () {
        return this.product.sku;
    };

    this.getBackOrderDate = function () {
        return this.product.backOrderDate;
    };

    this.isAdr = function () {
        return this.adr;
    };

    this.isRedeem = function () {
        return this.redeem;
    };

    this.isPartialRedeem = function () {
        return !!this.partialRedeem;
    }

    this.canToggleAdr = function () {
        return this.product.hasMultipleOrderTypes() && !this.businessPortfolio && !this.redeem;
    };

    this.isBusinessPortfolio = function () {
        return this.businessPortfolio;
    };

    this.getTotalPrice = function () {
        if (this.partialRedeem) {
            const pointQty = this.qtyRedeemWithPoints || 0;
            return (this.qty - pointQty) * this.product.getPrice();
        }else {
            return this.qty * this.product.getPrice();
        }
    };

    this.getTotalPriceFixed = function () {
        if (this.partialRedeem) {
            const pointQty = this.qtyRedeemWithPoints || 0;
            return parseFloat(Math.round(((this.qty - pointQty) * this.product.getPrice()) * 100) / 100).toFixed(2);
        } else {
            return parseFloat(Math.round((this.qty * this.product.getPrice()) * 100) / 100).toFixed(2);
        }
    };

    this.getSinglePv = function () {
        return this.isSapLineItem() ? (this.product.pv / this.qty).toFixed(2) * 1 : (this.product.pv || 0) * 1;
    };

    this.getTotalPsv = function () {
        if (this.partialRedeem) {
            const pointQty = this.qtyRedeemWithPoints || 0;
            return this.isSapLineItem() ? (this.product.pv || 0) : ((this.qty || 0) - pointQty) * (this.product.pv || 0);
        } else {
            return this.isSapLineItem() ? (this.product.pv || 0) : (this.qty || 0) * (this.product.pv || 0);
        }
    };

    this.getTotalPsvFixed = function () {
        if (this.partialRedeem) {
            const pointQty = this.qtyRedeemWithPoints || 0;
            return parseFloat(Math.round(((this.qty - pointQty) * (this.product.pv || 0)) * 100) / 100).toFixed(2);
        } else {
            return parseFloat(Math.round((this.qty * (this.product.pv || 0)) * 100) / 100).toFixed(2);
        }
    };

    this.getTotalCsv = function () {
        if (this.partialRedeem) {
            const pointQty = this.qtyRedeemWithPoints || 0;
            return (this.qty - pointQty) * (this.product.cv || 0);
        } else {
            return this.qty * (this.product.cv || 0);
        }
    };

    this.getTotalCsvFixed = function () {
        if (this.partialRedeem) {
            const pointQty = this.qtyRedeemWithPoints || 0;
            return parseFloat(Math.round(((this.qty - pointQty) * (this.product.cv || 0)) * 100) / 100).toFixed(2);
        } else {
            return parseFloat(Math.round((this.qty * (this.product.cv || 0)) * 100) / 100).toFixed(2);
        }
    };

    this.getPoints = function () {
        return this.qty * this.product.points;
    };

    this.isSapLineItem = function () {
        return this.sapLineId != null;
    };

    this.isAgelocme = function () {
        return this.product.agelocme != null && this.product.agelocme != undefined;
    };

    this.getMarketAttributes = function () {
        return this.product.marketAttributes;
    }

    this.toFlatJson = function() {
        let retVal = {};

        retVal.id = this.id;
        retVal.isSapLineItem = this.isSapLineItem();
        retVal.lang = this.product.lang;
        retVal.key = this.key;
        retVal.sku = this.product.sku;
        retVal.globalProductID = this.product.globalProductID;
        retVal.isAdr = this.adr;
        retVal.hasAdrOption = this.product.hasAdrOption();
        retVal.thumbnail = this.product.getThumbnail() || '';
        retVal.title = this.product.title || 'Title Not Found';
        retVal.description = this.product.getDescription();
        retVal.isBackOrdered = this.product.backOrderDate != null;
        retVal.backOrderedDate = this.product.backOrderDate;

        retVal.qty = this.getQty();
        retVal.maxQty = this.getMaxQty();
        retVal.productAvailableQty = this.product.availableQuantity;
        retVal.locallyProduced = this.product.locallyProduced;
        retVal.canToggleAdr = this.canToggleAdr();
        retVal.subPriceFlag = this.product.subPriceFlag;
        retVal.priceType = this.product.priceType;
        retVal.price = this.product.getPrice();
        retVal.csv = this.getTotalCsvFixed();
        retVal.singlePv = this.getSinglePv();
        retVal.psv = this.getTotalPsv();
        retVal.totalPrice = this.getTotalPrice();
        retVal.eventName = this.product.eventName;
        retVal.isExclusive = this.product.isExclusive;
        // TODO
        // retVal.atpProcessing = retVal.atpProcessing ? retVal.atpProcessing : '';
        retVal.isBusinessPortfolio = this.isBusinessPortfolio();
        retVal.canDelete = this.canDelete;
        retVal.agelocme = this.product.agelocme;
        retVal.isAgelocme = this.isAgelocme();
        retVal.bpOptions = [];
        if (retVal.isBusinessPortfolio) {
            this.alternateBusinessPortfolios.forEach(altBp => {
                retVal.bpOptions.push({
                    title: altBp.title,
                    sku: altBp.sku,
                    checked: false
                });
            });
        }
        retVal.totalPoints = this.getPoints();
        retVal.points = this.product.points;
        retVal.loyaltyPointsUsed = this.loyaltyPointsUsed;
        retVal.redeem = this.redeem;
        retVal.partialRedeem = this.partialRedeem;
        retVal.qtyRedeemWithPoints = this.qtyRedeemWithPoints;
        retVal.count = this.product.count;
        retVal.flavor = this.product.flavor;
        retVal.shade = this.product.shade;
        retVal.size = this.product.size;
        retVal.variants = this.product.variants;
        retVal.marketAttributes = this.product.marketAttributes;

        return retVal;
    };
}
